import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, FileText, Download, Settings, Plus, X } from "lucide-react";
import { API } from "@/contexts/AuthContext";

const PartsPackView = () => {
  const { partsPackId } = useParams();
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingPart, setEditingPart] = useState(null);
  const [addingPart, setAddingPart] = useState(null);
  const [copyText, setCopyText] = useState("");
  const navigate = useNavigate();

  const statusColors = {
    not_ordered: "bg-gray-200 text-gray-700",
    ordered: "bg-blue-200 text-blue-700",
    received: "bg-amber-200 text-amber-700",
    fitted: "bg-green-200 text-green-700"
  };

  const statusLabels = {
    not_ordered: "Not Ordered",
    ordered: "Ordered",
    received: "Received",
    fitted: "Fitted"
  };

  useEffect(() => {
    fetchPack();
  }, [partsPackId]);

  const fetchPack = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/foh/parts-pack/${partsPackId}`);
      setPack(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to load Parts Pack");
      navigate("/app/foh");
    } finally {
      setLoading(false);
    }
  };

  const updatePartStatus = async (partId, newStatus) => {
    try {
      await axios.put(`${API}/foh/parts-pack/${partsPackId}/part/${partId}/status`, {
        status: newStatus
      });
      fetchPack();
    } catch (err) {
      alert("Failed to update status");
    }
  };

  const updatePartSupplier = async (partId, supplierData) => {
    try {
      await axios.put(`${API}/foh/parts-pack/${partsPackId}/part/${partId}/supplier`, supplierData);
      setEditingPart(null);
      fetchPack();
    } catch (err) {
      alert("Failed to update supplier info");
    }
  };

  const addCustomPart = async (jobItemId, partType, name, quantity, notes) => {
    try {
      await axios.post(`${API}/foh/parts-pack/${partsPackId}/add-part`, {
        job_item_id: jobItemId,
        type: partType,
        name,
        quantity,
        notes
      });
      setAddingPart(null);
      fetchPack();
    } catch (err) {
      alert("Failed to add part");
    }
  };

  const exportAsText = async () => {
    try {
      const res = await axios.get(`${API}/foh/parts-pack/${partsPackId}/export`);
      setCopyText(res.data.text);
    } catch (err) {
      alert("Failed to export");
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(copyText)
      .then(() => alert("Copied to clipboard!"))
      .catch(() => alert("Failed to copy. Please select and copy manually."));
  };

  const printPack = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!pack) return null;

  const summary = { total: 0, not_ordered: 0, ordered: 0, received: 0, fitted: 0 };
  pack.job_items?.forEach(jobItem => {
    [...(jobItem.parts || []), ...(jobItem.consumables || [])].forEach(part => {
      summary.total++;
      summary[part.status || "not_ordered"]++;
    });
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6 print:bg-white print:p-2">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-start mb-6 print:mb-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <FileText className="w-6 h-6" />
              Parts Pack
            </h1>
            <div className="mt-2 text-slate-600">
              <span className="font-semibold text-lg">{pack.reg}</span>
              <span className="mx-2">•</span>
              <span>{pack.make} {pack.model}</span>
            </div>
            {pack.customer_name && (
              <p className="text-sm text-slate-500">{pack.customer_name}</p>
            )}
          </div>
          <div className="flex gap-2 print:hidden">
            <Button variant="outline" size="sm" onClick={() => navigate("/app/foh")} data-testid="parts-pack-back-btn">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button variant="outline" size="sm" onClick={exportAsText} data-testid="parts-pack-export-btn">
              <Download className="w-4 h-4 mr-1" /> Export
            </Button>
            <Button variant="outline" size="sm" onClick={printPack} data-testid="parts-pack-print-btn">
              Print
            </Button>
          </div>
        </div>

        <Card className="mb-6 print:mb-2 print:shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-700">Progress</h3>
              <div className="flex gap-4 text-sm">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-gray-300"></span>
                  Not Ordered: {summary.not_ordered}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-blue-400"></span>
                  Ordered: {summary.ordered}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-amber-400"></span>
                  Received: {summary.received}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-green-500"></span>
                  Fitted: {summary.fitted}
                </span>
              </div>
            </div>
            <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden flex">
              <div className="bg-gray-400 h-full" style={{ width: `${(summary.not_ordered / summary.total) * 100}%` }}></div>
              <div className="bg-blue-500 h-full" style={{ width: `${(summary.ordered / summary.total) * 100}%` }}></div>
              <div className="bg-amber-500 h-full" style={{ width: `${(summary.received / summary.total) * 100}%` }}></div>
              <div className="bg-green-500 h-full" style={{ width: `${(summary.fitted / summary.total) * 100}%` }}></div>
            </div>
          </CardContent>
        </Card>

        {pack.job_items?.map((jobItem, jobIdx) => (
          <Card key={jobItem.id} className="mb-4 print:mb-2 print:shadow-none print:break-inside-avoid">
            <CardHeader className="pb-2 print:pb-1">
              <CardTitle className="text-lg">{jobItem.job_name}</CardTitle>
            </CardHeader>
            <CardContent>
              {jobItem.parts?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-slate-500 mb-2 uppercase">Parts</h4>
                  <div className="space-y-2">
                    {jobItem.parts.map((part, partIdx) => (
                      <div key={part.id} className="border rounded p-3 bg-white">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{part.quantity}x {part.name}</span>
                              {part.notes && (
                                <span className="text-xs text-slate-500 italic">({part.notes})</span>
                              )}
                            </div>
                            {part.supplier && (
                              <p className="text-sm text-slate-500 mt-1">
                                Supplier: {part.supplier}
                                {part.order_ref && ` • Ref: ${part.order_ref}`}
                                {part.delivery_date && ` • ETA: ${part.delivery_date}`}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 print:hidden">
                            <select
                              value={part.status || "not_ordered"}
                              onChange={(e) => updatePartStatus(part.id, e.target.value)}
                              className={`text-xs px-2 py-1 rounded border ${statusColors[part.status || "not_ordered"]}`}
                              data-testid={`part-status-${part.id}`}
                            >
                              <option value="not_ordered">Not Ordered</option>
                              <option value="ordered">Ordered</option>
                              <option value="received">Received</option>
                              <option value="fitted">Fitted</option>
                            </select>
                            <Button variant="ghost" size="sm" onClick={() => setEditingPart(part)}>
                              <Settings className="w-3 h-3" />
                            </Button>
                          </div>
                          <Badge className={`print:inline hidden ${statusColors[part.status || "not_ordered"]}`}>
                            {statusLabels[part.status || "not_ordered"]}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {jobItem.consumables?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-slate-500 mb-2 uppercase">Consumables</h4>
                  <div className="space-y-2">
                    {jobItem.consumables.map((cons, consIdx) => (
                      <div key={cons.id} className="border rounded p-3 bg-slate-50">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <span>{cons.quantity}x {cons.name}</span>
                            {cons.notes && (
                              <span className="text-xs text-slate-500 italic">({cons.notes})</span>
                            )}
                          </div>
                          <select
                            value={cons.status || "not_ordered"}
                            onChange={(e) => updatePartStatus(cons.id, e.target.value)}
                            className={`text-xs px-2 py-1 rounded border print:hidden ${statusColors[cons.status || "not_ordered"]}`}
                          >
                            <option value="not_ordered">Not Ordered</option>
                            <option value="ordered">Ordered</option>
                            <option value="received">Received</option>
                            <option value="fitted">Fitted</option>
                          </select>
                          <Badge className={`print:inline hidden ${statusColors[cons.status || "not_ordered"]}`}>
                            {statusLabels[cons.status || "not_ordered"]}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button
                variant="ghost"
                size="sm"
                className="text-slate-500 print:hidden"
                onClick={() => setAddingPart({ jobItemId: jobItem.id, type: "parts" })}
              >
                <Plus className="w-3 h-3 mr-1" /> Add Part
              </Button>
            </CardContent>
          </Card>
        ))}

        <div className="text-center text-xs text-slate-400 mt-6 print:mt-2">
          Parts Pack generated {new Date(pack.created_at).toLocaleDateString("en-GB")}
          <br />
          This is an assistive checklist — not an order system
        </div>

        {editingPart && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 print:hidden">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Supplier & Order Info</CardTitle>
                <p className="text-sm text-slate-500">{editingPart.name}</p>
              </CardHeader>
              <CardContent>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  updatePartSupplier(editingPart.id, {
                    supplier: formData.get("supplier"),
                    order_ref: formData.get("order_ref"),
                    delivery_date: formData.get("delivery_date")
                  });
                }}>
                  <div className="space-y-4">
                    <div>
                      <Label>Supplier Name</Label>
                      <Input name="supplier" defaultValue={editingPart.supplier} placeholder="e.g. Euro Car Parts" />
                    </div>
                    <div>
                      <Label>Order Reference</Label>
                      <Input name="order_ref" defaultValue={editingPart.order_ref} placeholder="e.g. ORD-12345" />
                    </div>
                    <div>
                      <Label>Estimated Delivery</Label>
                      <Input name="delivery_date" type="date" defaultValue={editingPart.delivery_date} />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setEditingPart(null)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1">Save</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {addingPart && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 print:hidden">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Add Custom Part</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  addCustomPart(
                    addingPart.jobItemId,
                    formData.get("type"),
                    formData.get("name"),
                    parseInt(formData.get("quantity")) || 1,
                    formData.get("notes")
                  );
                }}>
                  <div className="space-y-4">
                    <div>
                      <Label>Type</Label>
                      <select name="type" className="w-full p-2 border rounded">
                        <option value="parts">Part</option>
                        <option value="consumables">Consumable</option>
                      </select>
                    </div>
                    <div>
                      <Label>Part Name *</Label>
                      <Input name="name" required placeholder="e.g. Brake Caliper Bolt" />
                    </div>
                    <div>
                      <Label>Quantity</Label>
                      <Input name="quantity" type="number" defaultValue="1" min="1" />
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Input name="notes" placeholder="e.g. VIN check required" />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setAddingPart(null)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1">Add</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {copyText && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 print:hidden">
            <Card className="w-full max-w-2xl max-h-[80vh]">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Export Parts Pack</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setCopyText("")}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Textarea 
                  value={copyText} 
                  readOnly 
                  className="font-mono text-xs h-64"
                />
                <div className="flex gap-3 mt-4">
                  <Button variant="outline" className="flex-1" onClick={() => setCopyText("")} data-testid="export-close-btn">
                    Close
                  </Button>
                  <Button className="flex-1" onClick={copyToClipboard} data-testid="export-copy-btn">
                    Copy to Clipboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartsPackView;
